import { useCookieValue } from "@react-hookz/web/cjs/useCookieValue";
import React, { useState } from "react";
import { useSdkContext } from "~/components/contexts";
import { useNewsletterContext } from "~/components/newsletter/newsletter-widget/contexts";
import { Variant } from "~/components/newsletter/newsletter-widget/enums";
import { useNewsletterApi } from "~/components/newsletter/newsletter-widget/hooks";
import { useAccountsApi } from "~/lib/hooks";
import { notEmpty } from "~/lib/utils";
import { EmailForm } from "./email-form";
import { ActionComponent } from "./enums";
import { getTriggerPoint } from "./helpers";
import { SignUpButton } from "./sign-up-button";
import { SocialButtons } from "./social-buttons";
import { SocialType } from "./social-buttons/enums";
export const useSignUpNewsletter = () => {
  const {
    campaignName,
    dataMatrix,
    newsletterItems,
    variant
  } = useNewsletterContext();
  const {
    config,
    handleRegisterMixPanel,
    handleTrackGA,
    handleTrackGA4,
    handleTrackGTM,
    handleTrackMirror,
    handleTrackMixPanel,
    user
  } = useSdkContext();
  const {
    handleSignUpNewsletter
  } = useNewsletterApi();
  const {
    createUser
  } = useAccountsApi(config.accounts.host);
  const [loginType] = useCookieValue("scmp_user_login_type");
  const isLoggedIn = notEmpty(user === null || user === void 0 ? void 0 : user.useremail);
  const handleTrackMixPanelEvent = newsletterName => {
    var _valueMap$variant$loc, _valueMap$variant, _valueMap$variant$for, _valueMap$variant2;
    if (!isLoggedIn) return;

    // More from https://project.scmp.tech/product/web/scmp-pwa/-/blob/master/scmp-app/src/universal/components/Widgets/Newsletter/index.vue#L634 but we will not deal with it for now
    // until we need to add that specific newsletter section
    const valueMap = {
      [Variant.AcquisitionNewsletterPopup]: {
        format: "In-line",
        location: ""
      },
      [Variant.Article]: {
        format: "In-line",
        location: ""
      },
      [Variant.ArticleDark]: {
        format: "In-line",
        location: ""
      },
      [Variant.Footer]: {
        format: "In-line",
        location: ""
      },
      [Variant.Homepage]: {
        format: "In-line",
        location: "Homepage"
      },
      [Variant.Topic]: {
        format: "In-line",
        location: "Topic Index"
      },
      [Variant.LiveArticle]: {
        format: "In-line",
        location: "Article"
      },
      [Variant.NewsletterList]: {
        format: "NL List",
        location: "Newsletter Index"
      },
      [Variant.NewsletterArticle]: {
        format: "Sticky widget",
        location: "Newsletter Article"
      },
      [Variant.NewsletterArticleBottom]: {
        format: "Sticky widget",
        location: "Newsletter Article"
      },
      [Variant.Posties]: {
        format: "In-line",
        location: ""
      },
      [Variant.Section]: {
        format: "In-line",
        location: "Section Index"
      }
    };
    const property = {
      Location: (_valueMap$variant$loc = (_valueMap$variant = valueMap[variant]) === null || _valueMap$variant === void 0 ? void 0 : _valueMap$variant.location) !== null && _valueMap$variant$loc !== void 0 ? _valueMap$variant$loc : "",
      "Newsletters Signed-up": newsletterName,
      "Sign-up Format": (_valueMap$variant$for = (_valueMap$variant2 = valueMap[variant]) === null || _valueMap$variant2 === void 0 ? void 0 : _valueMap$variant2.format) !== null && _valueMap$variant$for !== void 0 ? _valueMap$variant$for : "In-line"
    };
    handleRegisterMixPanel(property);
    handleTrackMixPanel({
      name: "Newsletters Signed-up",
      property
    });
  };
  const handleSubmit = async email => {
    var _response$errors, _response$entity$uid$, _response$entity;
    if ((newsletterItems === null || newsletterItems === void 0 ? void 0 : newsletterItems.length) === 0) return {
      error: true
    };
    const response = await handleSignUpNewsletter(newsletterItems.map(newsletterItem => newsletterItem.entityId), email, dataMatrix);
    if ((_response$errors = response.errors) !== null && _response$errors !== void 0 && _response$errors.length) return {
      error: true
    };
    newsletterItems.forEach((newsletter, index) => {
      const handleCustomTrackGA = () => {
        var _loginType$toLowerCas2, _map$variant;
        // Acquisition popup variant will send one tracking event
        if (variant === Variant.AcquisitionNewsletterPopup) {
          var _loginType$toLowerCas;
          if (index > 0 || !campaignName) return;
          handleTrackGA({
            action: `Acquisition/${campaignName}/Register/Email`,
            category: "Acquisition",
            label: window.location.href
          });
          handleTrackGA4({
            action: "sys",
            category: "nl",
            customized_parameters: {
              internal_campaign: campaignName !== null && campaignName !== void 0 ? campaignName : "",
              method: isLoggedIn ? (_loginType$toLowerCas = loginType === null || loginType === void 0 ? void 0 : loginType.toLowerCase()) !== null && _loginType$toLowerCas !== void 0 ? _loginType$toLowerCas : "" : "email",
              newsletter_name: newsletter.name,
              trigger_point: "ppto"
            },
            subcategory: "sign_up"
          });
          return;
        }
        handleTrackGA4({
          action: "sys",
          category: "nl",
          customized_parameters: {
            internal_campaign: campaignName !== null && campaignName !== void 0 ? campaignName : "",
            method: isLoggedIn ? (_loginType$toLowerCas2 = loginType === null || loginType === void 0 ? void 0 : loginType.toLowerCase()) !== null && _loginType$toLowerCas2 !== void 0 ? _loginType$toLowerCas2 : "" : "email",
            newsletter_name: newsletter.name,
            trigger_point: getTriggerPoint(variant)
          },
          subcategory: "sign_up"
        });
        const map = {
          [Variant.Footer]: "Newsletter (Footer - Inline)",
          [Variant.Article]: "Newsletter (Article - Inline)",
          [Variant.ArticleDark]: "Newsletter (Article - Inline)",
          [Variant.Homepage]: "Newsletter (Homepage - Inline)",
          [Variant.Section]: "Newsletter (Section - Inline)",
          [Variant.Topic]: "Newsletter (Topic - Inline)",
          [Variant.LiveArticle]: "Newsletter (Article - Inline)",
          [Variant.NewsletterList]: "Newsletter (Index)",
          [Variant.NewsletterArticle]: "Newsletter (newsletter article)",
          [Variant.NewsletterArticleBottom]: "Newsletter (newsletter article)",
          [Variant.Posties]: "Newsletter (Brochure)"
        };
        handleTrackGA({
          action: "Newsletter Sign Up/Email",
          category: (_map$variant = map[variant]) !== null && _map$variant !== void 0 ? _map$variant : "Newsletter (Homepage - Inline)",
          label: newsletter.name
        });
      };
      handleCustomTrackGA();
      handleTrackMirror({
        event_name: "newsletter_sign_up",
        newsletter_subscribe: newsletter.name
      });
      handleTrackGTM({
        event: "newsletter-subscribe",
        "newsletter-id": newsletter.entityId,
        "newsletter-name": newsletter.name
      });
      handleTrackMixPanelEvent(newsletter.name);
    });
    return {
      error: false,
      result: {
        uid: (_response$entity$uid$ = (_response$entity = response.entity) === null || _response$entity === void 0 || (_response$entity = _response$entity.uid) === null || _response$entity === void 0 || (_response$entity = _response$entity.entity) === null || _response$entity === void 0 ? void 0 : _response$entity.uid) !== null && _response$entity$uid$ !== void 0 ? _response$entity$uid$ : ""
      }
    };
  };
  const handleSubmitWithEmail = async (email, recaptchaToken, isAgreedReceiveMarketing) => {
    try {
      var _response$result;
      const response = await handleSubmit(email);
      if (response.error) return response;else if (!((_response$result = response.result) !== null && _response$result !== void 0 && _response$result.uid)) {
        await createUser({
          newsletterId: JSON.stringify(newsletterItems.map(item => item.entityId)),
          newsletterName: JSON.stringify(newsletterItems.map(item => item.name)),
          optIn: isAgreedReceiveMarketing ? 1 : 0,
          recaptchaToken,
          recaptchaVersion: "v3",
          username: email
        });
        handleTrackGA4({
          action: "sys",
          category: "login",
          customized_parameters: {
            internal_campaign: campaignName !== null && campaignName !== void 0 ? campaignName : "",
            method: "email",
            result: "register_success",
            trigger_point: "ppto"
          },
          subcategory: "result"
        });
      }
    } catch {
      return {
        error: true
      };
    }
    return {
      error: false
    };
  };
  return {
    handleSubmit,
    handleSubmitWithEmail
  };
};
export const useAction = ({
  isAgreedReceiveMarketing,
  isDisableSignupButtonMouseOver,
  isShowOverlaySignUp,
  onSuccess
}) => {
  const {
    handleTrackGA: handleTrackGA_,
    handleTrackGA4,
    user
  } = useSdkContext();
  const {
    campaignName,
    newsletterItemForDisplay,
    newsletterItems,
    setLastSubscribedNewsletter,
    signUpButtonDisplayComponent,
    variant
  } = useNewsletterContext();
  const isLoggedIn = notEmpty(user === null || user === void 0 ? void 0 : user.useremail);
  const [loginType] = useCookieValue("scmp_user_login_type");
  const [actionComponent, setActionComponent] = useState(() => {
    if (isLoggedIn) return ActionComponent.SignUpButton;else if (isShowOverlaySignUp) return ActionComponent.OverlaySignUpButton;
    return ActionComponent.SocialButtons;
  });
  const [isLoading, setIsLoading] = useState(false);
  const handleSignUpClick = async () => {
    const handleTrackGA = () => {
      var _signUpButtonDisplayC;
      if (variant !== Variant.AcquisitionNewsletterPopup || !campaignName) {
        var _loginType$toLowerCas3, _newsletterItems$0$na;
        handleTrackGA4({
          action: "click",
          category: "nl",
          customized_parameters: {
            method: isLoggedIn ? (_loginType$toLowerCas3 = loginType === null || loginType === void 0 ? void 0 : loginType.toLowerCase()) !== null && _loginType$toLowerCas3 !== void 0 ? _loginType$toLowerCas3 : "" : "email",
            newsletter_name: (_newsletterItems$0$na = newsletterItems[0].name) !== null && _newsletterItems$0$na !== void 0 ? _newsletterItems$0$na : "",
            trigger_point: getTriggerPoint(variant)
          },
          subcategory: "sign_up"
        });
        return;
      }
      handleTrackGA_({
        action: `Acquisition/${campaignName}/Sign Up/Click`,
        category: "Acquisition",
        label: window.location.href
      });
      handleTrackGA4({
        action: "click",
        category: "promopopup",
        customized_parameters: {
          cta_type: "nl_signup",
          internal_campaign: campaignName,
          message: (_signUpButtonDisplayC = signUpButtonDisplayComponent === null || signUpButtonDisplayComponent === void 0 ? void 0 : signUpButtonDisplayComponent.toString()) !== null && _signUpButtonDisplayC !== void 0 ? _signUpButtonDisplayC : "SIGN UP NOW",
          step: "1"
        },
        subcategory: ""
      });
    };
    handleTrackGA();
    if (actionComponent === ActionComponent.OverlaySignUpButton) {
      setActionComponent(ActionComponent.SocialButtons);
    } else if (isLoggedIn && user !== null && user !== void 0 && user.useremail) {
      setIsLoading(true);
      const {
        error
      } = await handleSubmit(user.useremail);
      newsletterItemForDisplay && (setLastSubscribedNewsletter === null || setLastSubscribedNewsletter === void 0 ? void 0 : setLastSubscribedNewsletter(newsletterItemForDisplay.name));
      !error && onSuccess();
    }
  };
  const handleSignUpMouseover = () => {
    if (isDisableSignupButtonMouseOver || actionComponent !== ActionComponent.OverlaySignUpButton) return;
    setActionComponent(ActionComponent.SocialButtons);
  };
  const {
    handleSubmit,
    handleSubmitWithEmail
  } = useSignUpNewsletter();
  const handleRenderActionComponent = () => {
    switch (actionComponent) {
      case ActionComponent.SocialButtons:
        return /*#__PURE__*/React.createElement(SocialButtons, {
          isAgreedReceiveMarketing: isAgreedReceiveMarketing,
          onEmailClick: () => {
            setActionComponent(ActionComponent.EmailForm);
          }
        });
      case ActionComponent.EmailForm:
        return /*#__PURE__*/React.createElement(EmailForm, {
          onPreviousClick: () => setActionComponent(ActionComponent.SocialButtons),
          onSubmit: async ({
            email,
            recaptchaToken
          }) => {
            var _newsletterItems$0$na2;
            handleTrackGA4({
              action: "click",
              category: "nl",
              customized_parameters: {
                method: SocialType.Email,
                newsletter_name: (_newsletterItems$0$na2 = newsletterItems[0].name) !== null && _newsletterItems$0$na2 !== void 0 ? _newsletterItems$0$na2 : "",
                trigger_point: getTriggerPoint(variant)
              },
              subcategory: "sign_up"
            });
            const response = await handleSubmitWithEmail(email, recaptchaToken, isAgreedReceiveMarketing);
            !response.error && onSuccess();
            return response;
          }
        });
      default:
        return /*#__PURE__*/React.createElement(SignUpButton, {
          isLoading: isLoading,
          size: "large",
          onClick: () => void handleSignUpClick(),
          onMouseOver: handleSignUpMouseover
        }, signUpButtonDisplayComponent !== null && signUpButtonDisplayComponent !== void 0 ? signUpButtonDisplayComponent : "SIGN UP NOW");
    }
  };
  return {
    actionComponent,
    handleRenderActionComponent
  };
};